import { apiGet } from '../support/helpers/ApiConnection.Helper';
import ReferralStatusEntity from '../entities/ReferralStatus.Entity';

// Simple in-memory cache for referral statuses
let referralStatusesCache: {
    data: Array<ReferralStatusEntity> | null;
    timestamp: number | null;
    loading: boolean;
} = {
    data: null,
    timestamp: null,
    loading: false
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export default function getReferralStatuses(abortSignal: AbortSignal): Promise<Array<ReferralStatusEntity>> {
    // Check cache first
    if (referralStatusesCache.data && referralStatusesCache.timestamp) {
        const isExpired = (Date.now() - referralStatusesCache.timestamp) > CACHE_DURATION;
        if (!isExpired) {
            return Promise.resolve(referralStatusesCache.data);
        }
    }

    // If already loading, wait for the current request to finish
    if (referralStatusesCache.loading) {
        return new Promise((resolve, reject) => {
            const checkCache = setInterval(() => {
                if (!referralStatusesCache.loading) {
                    clearInterval(checkCache);
                    if (referralStatusesCache.data) {
                        resolve(referralStatusesCache.data);
                    } else {
                        reject(new Error('Failed to load referral statuses'));
                    }
                }
            }, 100);
        });
    }

    // Make new request
    referralStatusesCache.loading = true;
    return apiGet<Array<ReferralStatusEntity>>('/referral-statuses', abortSignal)
        .then(statuses => {
            referralStatusesCache.data = statuses;
            referralStatusesCache.timestamp = Date.now();
            referralStatusesCache.loading = false;
            return statuses;
        })
        .catch(error => {
            referralStatusesCache.loading = false;
            throw error;
        });
}

// Function to clear cache (useful for logout)
export function clearReferralStatusesCache() {
    referralStatusesCache.data = null;
    referralStatusesCache.timestamp = null;
    referralStatusesCache.loading = false;
}

// Function to refresh cache
export function refreshReferralStatuses(abortSignal: AbortSignal): Promise<Array<ReferralStatusEntity>> {
    referralStatusesCache.data = null;
    referralStatusesCache.timestamp = null;
    return getReferralStatuses(abortSignal);
}